"use client";

import React, { useState, useEffect } from "react";
import { Button } from "../../ui/button";
import { useArchivalMode } from "@/contexts/ArchivalModeContext";
import { getFY_YEAR } from "@/utils/sharepoint-env";
import { Input } from "../../ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Label } from "../../ui/label";
import { Checkbox } from "../../ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import {
  TrendingUp,
  TrendingDown,
  Minus,
  Play,
  Download,
  FileText,
  Settings,
  Calculator,
  BarChart3,
  MessageSquare,
  Users,
  Calendar,
  Search,
  Upload,
  ArrowLeft,
  Loader2,
  Save,
} from "lucide-react";
import { ReviewComment } from "../../shared/ReviewComment";
import { DocumentManager } from "../../planning/DocumentManager";
import { getCompanyName } from "@/lib/textron-data-processor";
import { 
  getProcedureFileUsage, 
  updateProcedureFileUsage,
  type FileUsageEntry 
} from "@/utils/fileUsageReference";

interface MoMAnalysisProps {
  onBack?: () => void;
}

interface EmployeeMoMData {
  employeeCode: string;
  employeeName: string;
  doj: string;
  dol: string;
  aprSepTotal: number;
  octMarTotal: number;
  variance: number;
  variancePercentage: number;
  remarks: string;
  isNewHire: boolean;
  isResigned: boolean;
}

interface ColumnSelection {
  displayColumns: string[];
  calculationColumns: string[];
}

const AVAILABLE_COLUMNS = [
  { value: "employee_code", label: "Employee Code", icon: <Users className="h-4 w-4" /> },
  { value: "employee_name", label: "Employee Name", icon: <Users className="h-4 w-4" /> },
  { value: "doj", label: "Date of Joining", icon: <Calendar className="h-4 w-4" /> },
  { value: "dol", label: "Date of Leaving", icon: <Calendar className="h-4 w-4" /> },
  { value: "basic", label: "Basic Salary", icon: <Calculator className="h-4 w-4" /> },
  { value: "hra", label: "HRA", icon: <Calculator className="h-4 w-4" /> },
  { value: "da", label: "DA", icon: <Calculator className="h-4 w-4" /> },
  { value: "allowances", label: "Allowances", icon: <Calculator className="h-4 w-4" /> },
  { value: "gross", label: "Gross Pay", icon: <Calculator className="h-4 w-4" /> },
  { value: "pf", label: "Provident Fund", icon: <Calculator className="h-4 w-4" /> },
  { value: "esi", label: "ESI", icon: <Calculator className="h-4 w-4" /> },
  { value: "deductions", label: "Total Deductions", icon: <Calculator className="h-4 w-4" /> },
  { value: "net_pay", label: "Net Pay", icon: <Calculator className="h-4 w-4" /> },
];

const MONTH_OPTIONS = [
  "Apr-24", "May-24", "Jun-24",
  "Jul-24", "Aug-24", "Sep-24", "Oct-24", "Nov-24", "Dec-24",
  "Jan-25", "Feb-25", "Mar-25"
];

export default function MoMAnalysis({ onBack }: MoMAnalysisProps) {
  const { isArchivalMode } = useArchivalMode();
  const [columnSelection, setColumnSelection] = useState<ColumnSelection>({
    displayColumns: [],
    calculationColumns: [],
  });
  const [incrementMonth, setIncrementMonth] = useState<string>("Oct-24");
  const [momData, setMomData] = useState<EmployeeMoMData[]>([]);
  const [processingStatus, setProcessingStatus] = useState<"idle" | "running" | "completed" | "error">("idle");
  const [showSelectedColumns, setShowSelectedColumns] = useState(false);
  const [payRegistrar, setPayRegistrar] = useState<string>("");
  const [payRegistrarColumns, setPayRegistrarColumns] = useState<string[]>([]);
  
  // SharePoint search and file management
  const [fileSearchStatus, setFileSearchStatus] = useState<"idle" | "searching" | "found" | "not_found">("idle");
  const [foundFiles, setFoundFiles] = useState<string[]>([]);
  const [referenceValue, setReferenceValue] = useState<string>("");
  const [uploadStatus, setUploadStatus] = useState<"idle" | "uploading" | "completed" | "error">("idle");
  
  const [showDisplayDropdown, setShowDisplayDropdown] = useState(false);
  const [showCalcDropdown, setShowCalcDropdown] = useState(false);
  const [displayColumnsSearch, setDisplayColumnsSearch] = useState("");
  const [calcColumnsSearch, setCalcColumnsSearch] = useState("");
  const [reviewCommentQuestionId, setReviewCommentQuestionId] = useState<string | null>(null);
  const [fileUsageReference, setFileUsageReference] = useState<FileUsageEntry[]>([]);
  const [companyName, setCompanyName] = useState<string>("");
  const [inferenceQuestion, setInferenceQuestion] = useState<any>({ textResponse: null, documents: [] });
  const [inferenceSaveStatus, setInferenceSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [inferenceSaveError, setInferenceSaveError] = useState<string | null>(null);

  // Generate question ID for a section
  const getSectionQuestionId = (sectionName: string): string => {
    const normalizedSection = sectionName
      .replace(/[^a-zA-Z0-9\s]/g, "")
      .replace(/\s+/g, "_")
      .toUpperCase();
    return `MOM_ANALYSIS_${normalizedSection}`;
  };

  const searchForFiles = async () => {
    setFileSearchStatus("searching");
    try {
      if (window.sharePointAPI?.loadComcFiles) {
        const result = await window.sharePointAPI.loadComcFiles("Execution_EBE");
        if (result.success && result.data?.files) {
          const files = result.data.files
            .map((f: any) => f.name)
            .filter((name: string) => name && name.trim().length > 0);
          if (files.length > 0) {
            setFoundFiles(files);
            setFileSearchStatus("found");
          } else {
            setFileSearchStatus("not_found");
          }
        } else {
          setFileSearchStatus("not_found");
        }
      } else {
        setFileSearchStatus("not_found");
      }
    } catch {
      setFileSearchStatus("not_found");
    }
  };

  // Load company name
  useEffect(() => {
    const loadCompanyName = async () => {
      try {
        const name = await getCompanyName("combined");
        setCompanyName(name || "ABC Private Limited");

      } catch (error) {
        console.warn('Failed to load company name, using fallback');
        setCompanyName("ABC Private Limited");
      }
    };
    loadCompanyName();
  }, []);

  // Load inference question from Execution_EBE_Responses.json
  useEffect(() => {
    const loadInferenceQuestion = async () => {
      try {
        const result = await (window as any).sharePointAPI?.downloadClientJson?.("Execution_EBE_Responses.json");
        if (result?.success && result?.data?.substantiveProcedures) {
          const inferenceId = "MOM-ANALYSIS-INFERENCE";
          const inferenceData = result.data.substantiveProcedures[inferenceId];
          if (inferenceData) {
            setInferenceQuestion({
              textResponse: inferenceData.textResponse || null,
              documents: (inferenceData.documents || []).map((docName: string) => ({
                id: `doc_${docName}`,
                type: 'linked',
                name: docName,
              })),
            });
          }
        }
      } catch (error) {
        console.warn("Failed to load inference question:", error);
      }
    };
    loadInferenceQuestion();
  }, []);

  // Auto-trigger search for files when component mounts (when user clicks on M-o-M Analysis panel)
  useEffect(() => {
    searchForFiles();
    loadFileUsageReference();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadFileUsageReference = async () => {
    try {
      const usage = await getProcedureFileUsage("M-o-M Analysis");
      setFileUsageReference(usage);
    } catch (error) {
      console.error("Error loading file usage reference:", error);
    }
  };

  const uploadFile = async () => {
    setUploadStatus("uploading");
    try {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.xlsx,.xls,.csv';
      input.onchange = async (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (file) {
          try {
            const base64Content = await new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => {
                const result = reader.result as string;
                const base64 = result.split(',')[1];
                resolve(base64);
              };
              reader.onerror = () => reject(new Error('Failed to read file'));
              reader.readAsDataURL(file);
            });

            if (window.sharePointAPI?.uploadFile) {
              // Get FY_YEAR from database dynamically
              const fyYear = getFY_YEAR();
              if (!fyYear) {
                throw new Error("FY_YEAR is not configured. Please ensure you have selected an engagement.");
              }

              const result = await window.sharePointAPI.uploadFile({
                fileContent: base64Content,
                fileName: file.name,
                referenceValue: referenceValue,
                folderName: "client",
                fyYear: fyYear,
                sectionList: ["Execution_EBE"],
              });

              if (result.success) {
                setFoundFiles([file.name]);
                setFileSearchStatus("found");
                setUploadStatus("completed");
              } else {
                setUploadStatus("error");
              }
            } else {
              setUploadStatus("error");
            }
          } catch {
            setUploadStatus("error");
          }
        }
      };
      input.click();
    } catch {
      setUploadStatus("error");
    }
  };

  const loadPayRegistrarColumns = async () => {
    try {
      if (window.payroll?.loadExcelColumns && payRegistrar) {
        const fileName = payRegistrar.includes(' (') ? payRegistrar.split(' (')[0] : payRegistrar;
        const res = await window.payroll.loadExcelColumns(fileName);
        if (res.ok && Array.isArray(res.columns)) {
          setPayRegistrarColumns(res.columns);
          // Save file name to file usage reference
          await updateProcedureFileUsage("M-o-M Analysis", [{ file_search: fileName }]);
          await loadFileUsageReference();
        }
      }
    } catch {}
  };

  const handleColumnToggle = (columnType: keyof ColumnSelection, columnValue: string) => {
    setColumnSelection(prev => ({
      ...prev,
      [columnType]: prev[columnType].includes(columnValue)
        ? prev[columnType].filter(col => col !== columnValue)
        : [...prev[columnType], columnValue]
    }));
  };

  const getColumnLabel = (value: string) => {
    return AVAILABLE_COLUMNS.find(col => col.value === value)?.label || value;
  };

  const getVarianceIcon = (variance: number) => {
    if (variance > 0) return <TrendingUp className="h-4 w-4 text-green-500" />;
    if (variance < 0) return <TrendingDown className="h-4 w-4 text-red-500" />;
    return <Minus className="h-4 w-4 text-gray-500" />;
  };

  const getVarianceColor = (variance: number) => {
    if (variance > 0) return "text-green-400";
    if (variance < 0) return "text-red-400";
    return "text-gray-400";
  };

  const runMoMAnalysis = async () => {
    // Save column selections to file usage reference
    const entries: FileUsageEntry[] = [];
    if (payRegistrar) {
      const fileName = payRegistrar.includes(' (') ? payRegistrar.split(' (')[0] : payRegistrar;
      entries.push({ file_search: fileName });
    }
    // Save display columns with their labels
    columnSelection.displayColumns.forEach(col => {
      const columnLabel = getColumnLabel(col);
      entries.push({ [`Display Column: ${columnLabel}`]: col });
    });
    // Save calculation columns with their labels
    columnSelection.calculationColumns.forEach(col => {
      const columnLabel = getColumnLabel(col);
      entries.push({ [`Calculation Column: ${columnLabel}`]: col });
    });
    if (entries.length > 0) {
      await updateProcedureFileUsage("M-o-M Analysis", entries);
      await loadFileUsageReference();
    }
    
    setProcessingStatus("running");
    try {
      if (window.payroll?.run) {
        const res = await window.payroll.run("execute_mom_increment_sharepoint", {
          inputFiles: [],
          options: {
            pay_registrar: payRegistrar,
            display_cols: columnSelection.displayColumns,
            calc_cols: columnSelection.calculationColumns,
            increment_month: incrementMonth,
          },
        });
        if (res.ok) {
          setProcessingStatus('completed');
        } else {
          setProcessingStatus('error');
        }
      } else {
        setProcessingStatus('error');
      }
    } catch {
      setProcessingStatus('error');
    }
  };

  const updateRemarks = (employeeCode: string, remarks: string) => {
    setMomData(prev =>
      prev.map(emp =>
        emp.employeeCode === employeeCode ? { ...emp, remarks } : emp
      )
    );
  };

  const canRunAnalysis = () => {
    return !!(payRegistrar && columnSelection.displayColumns.length > 0 && columnSelection.calculationColumns.length > 0 && incrementMonth);
  };

  // Save inference question
  const saveInferenceQuestion = async () => {
    if (!companyName) {
      setInferenceSaveError("Company name is required");
      setInferenceSaveStatus('error');
      return;
    }

    setInferenceSaveStatus('saving');
    setInferenceSaveError(null);

    try {
      // Auto-save any pending textbox content
      const documentManagers = document.querySelectorAll('[data-document-manager]');
      for (const manager of Array.from(documentManagers)) {
        const itemId = manager.getAttribute('data-item-id');
        if (itemId) {
          const autoSaveMethod = (window as any)[`autoSaveTextbox_${itemId}`];
          if (autoSaveMethod && typeof autoSaveMethod === 'function') {
            autoSaveMethod();
          }
        }
      }

      await new Promise(resolve => setTimeout(resolve, 1500));

      const areaPrefix = "EBE";
      const fileName = `Execution_${areaPrefix}_Responses.json`;
      
      let existingData: any = {};
      try {
        const downloadResult = await (window as any).sharePointAPI?.downloadClientJson?.(fileName);
        if (downloadResult?.success && downloadResult?.data) {
          existingData = downloadResult.data;
        }
      } catch (error) {
        console.warn(`Failed to download existing ${fileName}, starting from empty`, error);
      }

      if (!existingData || typeof existingData !== "object" || Array.isArray(existingData)) {
        existingData = {};
      }

      if (!existingData.substantiveProcedures) {
        existingData.substantiveProcedures = {};
      }

      // Save inference question - query db.json for section field like CustomProcedure
      const inferenceId = "MOM-ANALYSIS-INFERENCE";
      const sectionToFind = `Execution_${areaPrefix}_${inferenceId}`;
      let inferenceDocumentNames: string[] = [];
      try {
        if ((window as any).sharePointAPI?.loadDocumentListBySection) {
          const dbResult = await (window as any).sharePointAPI.loadDocumentListBySection(sectionToFind);
          if (dbResult?.success && dbResult?.data && Array.isArray(dbResult.data)) {
            inferenceDocumentNames = dbResult.data
              .map((file: any) => file.name || '')
              .filter((name: string) => name && name.trim().length > 0);
          }
        }
      } catch (error) {
        console.warn(`Failed to query db.json for section ${sectionToFind}:`, error);
      }

      const inferenceTextboxFromLocalStorage = localStorage.getItem(`textbox_${inferenceId}`);
      const hasInferenceTextboxContent = inferenceTextboxFromLocalStorage && inferenceTextboxFromLocalStorage.trim().length > 0;
      
      let inferenceTextResponseData = null;
      if (hasInferenceTextboxContent) {
        inferenceTextResponseData = {
          html: inferenceTextboxFromLocalStorage,
          plainText: inferenceTextboxFromLocalStorage.replace(/<[^>]*>/g, '')
        };
      } else if (inferenceQuestion.textResponse) {
        inferenceTextResponseData = inferenceQuestion.textResponse;
      }

      existingData.substantiveProcedures[inferenceId] = {
        selectedOption: "",
        textResponse: inferenceTextResponseData,
        documents: inferenceDocumentNames,
        table: null,
        lastUpdated: new Date().toISOString(),
        companyName,
      };

      const jsonString = JSON.stringify(existingData, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));

      const fyYear = getFY_YEAR();
      if (!fyYear) {
        throw new Error("FY_YEAR is not configured. Please ensure you have selected an engagement.");
      }

      const uploadResult = await (window as any).sharePointAPI?.uploadFile?.({
        fileContent: base64Content,
        fileName,
        referenceValue: `Execution ${areaPrefix} Substantive Responses`,
        folderName: "juggernaut",
        fyYear: fyYear,
        sectionList: [`Execution_${areaPrefix}_Responses`],
      });

      if (!uploadResult || !uploadResult.success) {
        throw new Error(uploadResult?.error || "Failed to save inference question");
      }

      setInferenceSaveStatus('saved');
      setTimeout(() => setInferenceSaveStatus('idle'), 3000);
    } catch (error) {
      console.error("Failed to save inference question:", error);
      setInferenceSaveError(error instanceof Error ? error.message : "Unknown error");
      setInferenceSaveStatus('error');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Month-on-Month Analysis</h2>
          <p className="text-gray-400">
            Analyze month-over-month payroll changes and variance trends
          </p>
        </div>
        {onBack && (
          <Button variant="outline" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        )}
      </div>

      {/* Step 1: Search for Existing Files */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3 text-white">
              <Search className="h-5 w-5 text-blue-500" />
              Step 1: Search for Existing Files
            </CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Step 1: Search for Existing Files"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            {fileSearchStatus === "searching" && (
              <div className="flex items-center gap-2 text-blue-400">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Searching for files...</span>
              </div>
            )}
            {fileSearchStatus === "found" && (
              <span className="text-green-400 font-bold"> FOUND!</span>
            )}
            {fileSearchStatus === "not_found" && (
              <span className="text-yellow-400">No files found</span>
            )}
            {fileSearchStatus === "idle" && (
              <span className="text-gray-400">Searching for files...</span>
            )}
          </div>

          {fileSearchStatus === "found" && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-white">Pay Register</Label>
                  <Select value={payRegistrar} onValueChange={setPayRegistrar}>
                    <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent className="border-white/10 bg-black/90 text-white max-h-64">
                      {foundFiles.map((f, i) => (<SelectItem key={i} value={f}>{f}</SelectItem>))}
                    </SelectContent>
                  </Select>
                  {fileUsageReference.find(e => e.file_search) && (
                    <p className="text-xs text-white/60 mt-1">
                      Last used: {fileUsageReference.find(e => e.file_search)?.file_search}
                    </p>
                  )}
                </div>
                <div className="flex items-end">
                  <Button onClick={loadPayRegistrarColumns} disabled={!payRegistrar} className="ml-auto">
                    Load Excel Columns
                  </Button>
                </div>
              </div>
            </div>
          )}

          {fileSearchStatus === "not_found" && (
            <div className="space-y-4 p-4 border border-yellow-500/20 bg-yellow-500/5 rounded-lg">
              <div className="text-yellow-400 font-medium">File not found. Please upload a file.</div>
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label className="text-white">Reference Value (optional)</Label>
                  <Input
                    value={referenceValue}
                    onChange={(e) => setReferenceValue(e.target.value)}
                    placeholder="Enter reference value"
                    className="border-white/10 bg-black/40 text-white"
                  />
                </div>
                <Button onClick={uploadFile} disabled={uploadStatus === "uploading"} className="bg-green-600 hover:bg-green-700 text-white">
                  {uploadStatus === "uploading" ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading...</>
                  ) : (
                    <><Upload className="h-4 w-4 mr-2" /> Upload File</>
                  )}
                </Button>
                {uploadStatus === "completed" && (
                  <span className="text-green-400">File uploaded successfully!</span>
                )}
                {uploadStatus === "error" && (
                  <span className="text-red-400">Upload failed</span>
                )}
              </div>
            </div>
          )}

        </CardContent>
      </Card>

      {/* Select Columns (Display & Calculation) */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3 text-white">
              <Settings className="h-5 w-5" />
              Step 2: Select Columns
            </CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Step 2: Select Columns"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Display Columns Dropdown */}
            <div className="space-y-2">
              <Label className="text-white">Display Columns</Label>
              <div className="relative">
                <Button variant="outline" className="w-full justify-between border-white/10" onClick={() => {
                  setShowDisplayDropdown(!showDisplayDropdown);
                  if (showDisplayDropdown) setDisplayColumnsSearch("");
                }}>
                  {columnSelection.displayColumns.length > 0 ? `${columnSelection.displayColumns.length} selected` : 'Select display columns'}
                </Button>
                {showDisplayDropdown && (
                  <div className="absolute z-10 mt-2 w-full rounded-md border border-white/10 bg-black/90 p-2 max-h-64 overflow-auto">
                    <div className="mb-2 sticky top-0 bg-black/90 z-10">
                      <Input
                        type="text"
                        placeholder="Search columns..."
                        value={displayColumnsSearch}
                        onChange={(e) => setDisplayColumnsSearch(e.target.value)}
                        className="w-full border-white/10 bg-black/40 text-white placeholder:text-white/40"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    {(payRegistrarColumns.length > 0 ? payRegistrarColumns : [])
                      .filter((c: any) => String(c).toLowerCase().includes(displayColumnsSearch.toLowerCase()))
                      .map((c: any) => (
                        <div key={String(c)} className="flex items-center gap-2 px-2 py-1 hover:bg-white/5 rounded">
                          <Checkbox id={`disp-${String(c)}`} checked={columnSelection.displayColumns.includes(String(c))} onCheckedChange={() => handleColumnToggle('displayColumns', String(c))} />
                          <Label htmlFor={`disp-${String(c)}`} className="text-white text-sm">{String(c)}</Label>
                        </div>
                      ))}
                  </div>
                )}
              </div>
              {columnSelection.displayColumns.length > 0 && (
                <div className="text-xs text-blue-300">{columnSelection.displayColumns.join(', ')}</div>
              )}
              {(() => {
                const lastUsedDisplayColumns = fileUsageReference
                  .filter(e => Object.keys(e)[0]?.startsWith("Display Column:"))
                  .map(e => Object.values(e)[0])
                  .filter((v): v is string => typeof v === 'string');
                return lastUsedDisplayColumns.length > 0 ? (
                  <p className="text-xs text-white/60 mt-1">
                    Last used: {lastUsedDisplayColumns.join(', ')}
                  </p>
                ) : null;
              })()}
            </div>

            {/* Calculation Columns Dropdown */}
            <div className="space-y-2">
              <Label className="text-white">Calculation Columns</Label>
              <div className="relative">
                <Button variant="outline" className="w-full justify-between border-white/10" onClick={() => {
                  setShowCalcDropdown(!showCalcDropdown);
                  if (showCalcDropdown) setCalcColumnsSearch("");
                }}>
                  {columnSelection.calculationColumns.length > 0 ? `${columnSelection.calculationColumns.length} selected` : 'Select calculation columns'}
                </Button>
                {showCalcDropdown && (
                  <div className="absolute z-10 mt-2 w-full rounded-md border border-white/10 bg-black/90 p-2 max-h-64 overflow-auto">
                    <div className="mb-2 sticky top-0 bg-black/90 z-10">
                      <Input
                        type="text"
                        placeholder="Search columns..."
                        value={calcColumnsSearch}
                        onChange={(e) => setCalcColumnsSearch(e.target.value)}
                        className="w-full border-white/10 bg-black/40 text-white placeholder:text-white/40"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    {(payRegistrarColumns.length > 0 ? payRegistrarColumns : [])
                      .filter((c: any) => String(c).toLowerCase().includes(calcColumnsSearch.toLowerCase()))
                      .map((c: any) => (
                        <div key={String(c)} className="flex items-center gap-2 px-2 py-1 hover:bg-white/5 rounded">
                          <Checkbox id={`calc-${String(c)}`} checked={columnSelection.calculationColumns.includes(String(c))} onCheckedChange={() => handleColumnToggle('calculationColumns', String(c))} />
                          <Label htmlFor={`calc-${String(c)}`} className="text-white text-sm">{String(c)}</Label>
                        </div>
                      ))}
                  </div>
                )}
              </div>
              {columnSelection.calculationColumns.length > 0 && (
                <div className="text-xs text-green-300">{columnSelection.calculationColumns.join(', ')}</div>
              )}
              {(() => {
                const lastUsedCalcColumns = fileUsageReference
                  .filter(e => Object.keys(e)[0]?.startsWith("Calculation Column:"))
                  .map(e => Object.values(e)[0])
                  .filter((v): v is string => typeof v === 'string');
                return lastUsedCalcColumns.length > 0 ? (
                  <p className="text-xs text-white/60 mt-1">
                    Last used: {lastUsedCalcColumns.join(', ')}
                  </p>
                ) : null;
              })()}
            </div>
          </div>

          {/* Increment Month Selection (unchanged) */}
          <div className="space-y-2">
            <Label className="text-white">Month of Increment</Label>
            <Select value={incrementMonth} onValueChange={setIncrementMonth}>
              <SelectTrigger>
                <SelectValue placeholder="Select increment month" />
              </SelectTrigger>
              <SelectContent className="border-white/10 bg-black/90 text-white">
                {MONTH_OPTIONS.map((month) => (
                  <SelectItem key={month} value={month}>
                    {month}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* MoM Analysis Results removed as requested */}

      {/* Execution Section */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <CardTitle className="flex items-center gap-3 text-white">
            <Play className="h-5 w-5" />
            Execute MoM Analysis
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">
                Run month-on-month analysis on the consolidated payroll register
              </p>
              {!canRunAnalysis() && (
                <p className="mt-1 text-xs text-red-400">
                  Please select display columns, calculation columns, and increment month
                </p>
              )}
            </div>
            <Button
              onClick={runMoMAnalysis}
              disabled={isArchivalMode || !canRunAnalysis() || processingStatus === "running"}
              className="bg-purple-600 hover:bg-purple-700 disabled:opacity-50"
            >
              {processingStatus === "running" ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Play className="mr-2 h-4 w-4" />
                  Run MoM Analysis
                </>
              )}
            </Button>
          </div>

          {/* Results */}
          {processingStatus === "completed" && (
            <div className="rounded border border-green-500/20 bg-green-500/10 p-4">
              <div className="flex items-center gap-3">
                <FileText className="h-5 w-5 text-green-500" />
                <div>
                  <h4 className="font-medium text-green-300">MoM Analysis Completed</h4>
                  <p className="text-sm text-green-200">
                    Month-on-month analysis has been completed successfully
                  </p>
                </div>
              </div>
              <div className="mt-3 flex gap-2">
                <Button variant="outline" className="border-green-500/30 text-green-300">
                  <Download className="mr-2 h-4 w-4" />
                  Download Excel Report
                </Button>
                <Button variant="outline" className="border-green-500/30 text-green-300">
                  <FileText className="mr-2 h-4 w-4" />
                  View Detailed Analysis
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Inference and Conclusion Question */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white">Document the inference and conclusion from the above procedure performed.</CardTitle>
            <div className="flex items-center gap-2">
              {inferenceSaveStatus === 'saved' && (
                <span className="text-sm text-green-400">Saved!</span>
              )}
              {inferenceSaveStatus === 'error' && inferenceSaveError && (
                <span className="text-sm text-red-400">Error</span>
              )}
              <Button
                onClick={saveInferenceQuestion}
                disabled={inferenceSaveStatus === 'saving' || !companyName}
                size="sm"
                className="bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
              >
                {inferenceSaveStatus === 'saving' ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <DocumentManager
            sectionKey="payroll"
            itemId="MOM-ANALYSIS-INFERENCE"
            itemQuestion="Document the inference and conclusion from the above procedure performed."
            itemTextResponse={inferenceQuestion.textResponse}
            itemDocuments={inferenceQuestion.documents || []}
            onTextResponseChange={(textResponse) => setInferenceQuestion({ ...inferenceQuestion, textResponse })}
            onDocumentsChange={(documents) => setInferenceQuestion({ ...inferenceQuestion, documents })}
            disabled={false}
          />
        </CardContent>
      </Card>

      {/* Review Comment Modal */}
      {reviewCommentQuestionId && (
        <ReviewComment
          questionId={reviewCommentQuestionId}
          questionText={(() => {
            // Map questionId back to section name for display
            const sectionMap: Record<string, string> = {
              [getSectionQuestionId("Step 1: Search for Existing Files")]: "Step 1: Search for Existing Files",
              [getSectionQuestionId("Step 2: Select Columns")]: "Step 2: Select Columns",
            };
            return sectionMap[reviewCommentQuestionId] || reviewCommentQuestionId;
          })()}
          isOpen={!!reviewCommentQuestionId}
          onClose={() => setReviewCommentQuestionId(null)}
        />
      )}
    </div>
  );
}
